import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';

import {
  AdminTaskRow,
  adminTaskApi,
  RollbackTaskPayload,
} from '../../../../api/adminTaskApi';
import { colors, primaryButtonSx, secondaryButtonSx, typo } from '../../../../theme/tokens';
import { WriteButton } from '../../../../components/ui';

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

export interface RollbackDialogProps {
  open: boolean;
  onClose: () => void;
  /** Task code (used in copy + endpoint URL). */
  taskCode: string;
  /** Version we're restoring *to* (contents get copied forward). */
  targetVersion: number;
  /** Current version at the time the dialog opened — optimistic-lock guard. */
  currentVersion: number;
  /** Called on 2xx so caller can refetch + show its own snackbar. */
  onSuccess: (row: AdminTaskRow) => void;
  /**
   * Verb shown in the title. Defaults to "Restore" (version history
   * drawer). Edit page passes "Discard" when rolling back to the last
   * published version.
   */
  verb?: 'Restore' | 'Discard';
}

/* ------------------------------------------------------------------ */
/*  Dialog                                                             */
/* ------------------------------------------------------------------ */

const RollbackDialog: React.FC<RollbackDialogProps> = ({
  open,
  onClose,
  taskCode,
  targetVersion,
  currentVersion,
  onSuccess,
  verb = 'Restore',
}) => {
  // Local 409 banner — mirrors the edit page's conflict UX so the user
  // sees the *same* "Someone else saved vN" copy whether they conflict
  // during an edit or during a rollback.
  const [conflict, setConflict] = useState<number | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      const payload: RollbackTaskPayload = {
        target_version: targetVersion,
        base_version: currentVersion,
      };
      return adminTaskApi.rollback(taskCode, payload);
    },
    onSuccess: (row) => {
      setConflict(null);
      onSuccess(row);
    },
    onError: (err: any) => {
      const status = err?.response?.status;
      const data = err?.response?.data;
      if (status === 409 && typeof data?.current_version === 'number') {
        setConflict(data.current_version);
      }
    },
  });

  const handleClose = () => {
    if (mutation.isPending) return;
    setConflict(null);
    onClose();
  };

  // Future version number that'll be created by the rollback. Server is
  // authoritative, but showing `currentVersion + 1` matches the mental
  // model from the UX brief ("A new version v6 will be created copying
  // v3's data").
  const futureVersion = currentVersion + 1;
  const isDiscard = verb === 'Discard';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, fontFamily: 'Manrope, sans-serif' }}>
        {verb}{' '}
        <Box component="code" sx={{ ...typo.mono, fontSize: 14 }}>
          {taskCode}
        </Box>{' '}
        to v{targetVersion}?
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ fontSize: 13, color: colors.textSecondary }}>
          {isDiscard ? (
            <>
              Your unpublished changes will be discarded. A new version{' '}
              <strong>v{futureVersion}</strong> will be created copying v{targetVersion}
              &apos;s data, so history stays append-only.
            </>
          ) : (
            <>
              A new version <strong>v{futureVersion}</strong> will be created copying v
              {targetVersion}&apos;s data. Nothing is destroyed — the existing versions
              stay in history.
            </>
          )}
        </DialogContentText>
        {/*
          R4 copy fix: call out that a restore is a draft-only change —
          tenants keep seeing whatever platform_version they're on until a
          separate Publish step runs. Users confused "Restore" with "Revert
          tenants", and we had one incident where someone thought they'd
          rolled back a tenant-facing bug by restoring here.
        */}
        <DialogContentText
          sx={{ fontSize: 12, color: colors.textTertiary, mt: 1.5, fontStyle: 'italic' }}
        >
          Note: this does not affect tenants. platform_task_id (or
          platform_adventure_id) and platform_version stay at their current values.
          Publish the restored version to propagate to tenants.
        </DialogContentText>

        {conflict != null && (
          <Alert severity="warning" sx={{ mt: 2, borderRadius: '8px' }}>
            Someone else saved v{conflict}. Close and reload to pick up the latest.
          </Alert>
        )}

        {mutation.isError && conflict == null && (
          <Alert severity="error" sx={{ mt: 2, borderRadius: '8px' }}>
            {verb} failed — try again.
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} sx={secondaryButtonSx} disabled={mutation.isPending}>
          Cancel
        </Button>
        <WriteButton
          variant="contained"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending || conflict != null}
          sx={{
            ...primaryButtonSx,
            // Admin-slate primary per UX brief (not amber brand) — the
            // brief explicitly calls out "slate primary" here because
            // destructive-but-reversible ops live in the admin surface.
            backgroundColor: colors.adminAccent,
            color: colors.bgWhite,
            '&:hover': { backgroundColor: colors.adminAccent, opacity: 0.92 },
          }}
        >
          {mutation.isPending ? `${verb.slice(0, -1)}ing…` : verb}
        </WriteButton>
      </DialogActions>
    </Dialog>
  );
};

export default RollbackDialog;
